import type { BackendError } from '../../errors'

/**
 * Default LiteLLM proxy for SafeAI Plus. The user can override it in the
 * connection dialog (or via VITE_LITELLM_BASE_URL at build time).
 */
export const DEFAULT_LITELLM_BASE_URL = 'https://litellm.safeaiplus.de'

/**
 * Everything needed to talk to a LiteLLM proxy. The add-in speaks the
 * OpenAI-compatible surface (`/v1/models`, `/v1/chat/completions`) directly —
 * there is no proxy of our own in between, so the Virtual Key is the only
 * credential.
 */
export interface LiteLlmConfig {
  /** Proxy root, e.g. https://litellm.safeaiplus.de */
  baseUrl: string
  /** LiteLLM Virtual Key ("sk-…") — sent as `Authorization: Bearer …`. */
  virtualKey: string
  /** Model name as listed by the proxy (`/v1/models`). */
  model: string
  /** Optional separate model for the composer's "Hirn"/thinking mode. */
  thinkingModel?: string
  /** Injectable fetch (tests / polyfills). Defaults to global fetch. */
  fetch?: typeof fetch
  /** Per-request timeout in ms (default 60000 for chat, 15000 for /v1/models). */
  timeoutMs?: number
  /** Telemetry hook — called for every normalized error. */
  onError?: (err: BackendError) => void
}

/** Connection half of the config — enough to list models / test the key. */
export type LiteLlmConnection = Pick<
  LiteLlmConfig,
  'baseUrl' | 'virtualKey' | 'fetch' | 'timeoutMs' | 'onError'
>

/**
 * Make a user-typed base URL safe for `new URL(path, base)`: without the
 * trailing slash a path-prefixed proxy ("https://host/litellm") would lose its
 * prefix, so every request path stays relative and the base keeps its slash.
 */
export function normalizeBaseUrl(baseUrl: string): string {
  const trimmed = baseUrl.trim()
  const withScheme = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  return withScheme.endsWith('/') ? withScheme : `${withScheme}/`
}
